import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminPendingRegistrations = () => {
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchPendingRegistrations();
  }, []);

  const fetchPendingRegistrations = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/pending-registrations`);
      setRegistrations(response.data);
    } catch (error) {
      toast.error('Fehler beim Laden der ausstehenden Registrierungen');
    } finally {
      setLoading(false);
    }
  };

  const handleActivate = async (userId, userName) => {
    if (!window.confirm(`Möchten Sie das Konto von "${userName}" wirklich aktivieren?`)) {
      return;
    }

    setActionLoading(userId);
    try {
      await axios.post(`${API_URL}/api/admin/pending-registrations/${userId}/activate`);
      toast.success(`Konto von ${userName} wurde aktiviert`);
      fetchPendingRegistrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktivieren');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (userId, userName) => {
    if (!window.confirm(`Möchten Sie die Registrierung von "${userName}" wirklich ablehnen und löschen?`)) {
      return;
    }

    setActionLoading(userId);
    try {
      await axios.post(`${API_URL}/api/admin/pending-registrations/${userId}/reject`);
      toast.success(`Registrierung von ${userName} wurde abgelehnt`);
      fetchPendingRegistrations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Ablehnen');
    } finally {
      setActionLoading(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A059]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="pending-registrations">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Ausstehende Registrierungen</h2>
          <p className="text-gray-600 mt-1">
            {registrations.length === 0 
              ? 'Keine ausstehenden Registrierungen'
              : `${registrations.length} Registrierung${registrations.length !== 1 ? 'en' : ''} warten auf Aktivierung`
            }
          </p>
        </div>
        <button
          onClick={fetchPendingRegistrations}
          className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Aktualisieren</span>
        </button>
      </div>

      {registrations.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-12 text-center">
          <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <p className="text-gray-500">Alle Registrierungen wurden bearbeitet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {registrations.map((reg) => (
            <div 
              key={reg.id} 
              className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              data-testid={`pending-registration-${reg.id}`}
            >
              <div className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-[#C5A059] bg-opacity-10 rounded-full flex items-center justify-center">
                        <span className="text-[#C5A059] font-bold text-lg">
                          {reg.name?.charAt(0)?.toUpperCase() || '?'}
                        </span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{reg.name}</h3>
                        <p className="text-sm text-gray-500">{reg.email}</p>
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Telefon</p>
                        <p className="font-medium">{reg.phone || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Geplante Investition</p>
                        <p className="font-bold text-[#C5A059] text-lg">
                          {reg.planned_investment_eur ? formatCurrency(reg.planned_investment_eur) : '-'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">IBAN</p>
                        <p className="font-mono text-sm">{reg.bank_iban || '-'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500 uppercase tracking-wider">Registriert am</p>
                        <p className="text-sm">{formatDate(reg.created_at)}</p>
                      </div>
                    </div>

                    {reg.bank_name && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500 mb-1">Bankverbindung</p>
                        <p className="text-sm">
                          <strong>{reg.bank_account_holder}</strong> • {reg.bank_name} • BIC: {reg.bank_bic}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col space-y-2 ml-6">
                    <button
                      onClick={() => handleActivate(reg.id, reg.name)}
                      disabled={actionLoading === reg.id}
                      className="px-4 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center space-x-2"
                      data-testid={`activate-btn-${reg.id}`}
                    >
                      {actionLoading === reg.id ? (
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      <span>Aktivieren</span>
                    </button>
                    <button
                      onClick={() => handleReject(reg.id, reg.name)}
                      disabled={actionLoading === reg.id}
                      className="px-4 py-2 border border-red-300 text-red-600 font-medium rounded-lg hover:bg-red-50 disabled:opacity-50 flex items-center space-x-2"
                      data-testid={`reject-btn-${reg.id}`}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      <span>Ablehnen</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPendingRegistrations;
